import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ImageCanvas } from './components/ImageCanvas';
import { Sidebar } from './components/Sidebar';
import { DirectorySelector, DirectoryStats } from './components/DirectorySelector';
import { ZoomControls } from './components/ZoomControls';
import { ClassEditModal } from './components/ClassEditModal';
import { AnnotationTooltip } from './components/AnnotationTooltip';
import { AnnotationEditor } from './components/AnnotationEditor';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { ApiService } from './services/api';
import {
  ImageInfo,
  ClassInfo,
  Annotation,
  Tool,
  BoundingBox,
  AnnotationStats,
  ViewTransform
} from './types';
import { AlertCircle, Loader2 } from 'lucide-react';

function App() {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // State
  const [directorySet, setDirectorySet] = useState(false);
  const [directoryPath, setDirectoryPath] = useState<string | null>(null);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [classes, setClasses] = useState<ClassInfo[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<number | null>(null);
  const [currentTool, setCurrentTool] = useState<Tool>(Tool.BBOX);
  const [currentClass, setCurrentClass] = useState(0);
  const [stats, setStats] = useState<AnnotationStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transform, setTransform] = useState<ViewTransform>({ x: 0, y: 0, scale: 1 });
  const [editingClass, setEditingClass] = useState<ClassInfo | null>(null);
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Handle directory selection
  const handleDirectorySet = async (path: string, dirStats: DirectoryStats) => {
    setDirectoryPath(path);
    setDirectorySet(true);

    // Load initial data after directory is set
    try {
      setIsLoading(true);
      setError(null);

      // Load images and classes
      const [imagesData, classesData, statsData] = await Promise.all([
        ApiService.fetchImages(),
        ApiService.fetchClasses(),
        ApiService.fetchStats()
      ]);

      setImages(imagesData.images);

      // טעינת שמות קלאסים מותאמים אישית מ-localStorage
      const savedClassNames = localStorage.getItem('customClassNames');
      let finalClasses = classesData;
      if (savedClassNames) {
        try {
          const customNames = JSON.parse(savedClassNames);
          finalClasses = classesData.map(cls => ({
            ...cls,
            name: customNames[cls.id] || cls.name
          }));
        } catch (e) {
          console.error('Failed to parse custom class names:', e);
        }
      }
      setClasses(finalClasses);
      setStats(statsData);

      // Load annotations for first image
      if (imagesData.images.length > 0) {
        await loadAnnotations(imagesData.images[0].filename);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Load annotations for current image
  const loadAnnotations = async (filename: string) => {
    try {
      const data = await ApiService.fetchAnnotations(filename);
      setAnnotations(data.annotations);
      setSelectedAnnotation(null);
    } catch (err) {
      console.error('Failed to load annotations:', err);
      setAnnotations([]);
    }
  };

  // Save annotations
  const saveAnnotations = useCallback(async () => {
    if (images.length === 0) return;

    try {
      setIsSaving(true);
      const currentImage = images[currentImageIndex];
      await ApiService.saveAnnotations(currentImage.filename, annotations);

      // Update stats
      const statsData = await ApiService.fetchStats();
      setStats(statsData);

      // Update image info
      const updatedImages = [...images];
      updatedImages[currentImageIndex] = {
        ...updatedImages[currentImageIndex],
        has_labels: annotations.length > 0,
        label_count: annotations.length
      };
      setImages(updatedImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save annotations');
    } finally {
      setIsSaving(false);
    }
  }, [images, currentImageIndex, annotations]);

  // Navigation
  const goToPrevious = useCallback(() => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      loadAnnotations(images[newIndex].filename);
    }
  }, [currentImageIndex, images]);

  const goToNext = useCallback(() => {
    if (currentImageIndex < images.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      loadAnnotations(images[newIndex].filename);
    }
  }, [currentImageIndex, images]);

  // Zoom control functions
  const handleZoomIn = useCallback(() => {
    setTransform(prev => ({
      ...prev,
      scale: Math.min(5, prev.scale * 1.2)
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setTransform(prev => ({
      ...prev,
      scale: Math.max(0.1, prev.scale / 1.2)
    }));
  }, []);

  const handleZoomReset = useCallback(() => {
    setTransform(prev => ({ ...prev, scale: 1 }));
  }, []);

  const handleFitToScreen = useCallback(() => {
    // This will be handled by ImageCanvas fitImageToCanvas
    setTransform({ x: 0, y: 0, scale: 0.9 });
  }, []);

  const handleZoomChange = useCallback((zoom: number) => {
    setTransform(prev => ({ ...prev, scale: zoom }));
  }, []);

  const handleClassEdit = useCallback((classId: number, newName: string) => {
    const updatedClasses = classes.map(cls => 
      cls.id === classId ? { ...cls, name: newName } : cls
    );
    setClasses(updatedClasses);
    
    // שמור ב-localStorage
    const customNames: Record<number, string> = {};
    updatedClasses.forEach(cls => {
      customNames[cls.id] = cls.name;
    });
    localStorage.setItem("customClassNames", JSON.stringify(customNames));
  }, [classes]);

  const handleClassAdd = useCallback(() => {
    const newId = Math.max(...classes.map(c => c.id), -1) + 1;
    const newClass: ClassInfo = {
      id: newId,
      name: `Class ${newId}`,
    };
    setClasses([...classes, newClass]);
  }, [classes]);

  const handleClassDelete = useCallback((classId: number) => {
    setClasses(classes.filter(cls => cls.id !== classId));
    // גם מחק annotations של ה-class הזה
    setAnnotations(annotations.filter(ann => ann.class_id !== classId));
  }, [classes, annotations]);

  const handleClassSave = useCallback((classId: number, newName: string) => {
    // עדכון רשימת הקלאסים
    const updatedClasses = classes.map(cls =>
      cls.id === classId ? { ...cls, name: newName } : cls
    );
    setClasses(updatedClasses);

    // שמירה ב-localStorage
    const classNames: Record<number, string> = {};
    updatedClasses.forEach(cls => {
      classNames[cls.id] = cls.name;
    });
    localStorage.setItem('customClassNames', JSON.stringify(classNames));
  }, [classes]);

  const handleAnnotationEdit = useCallback((annotationId: number) => {
    const annotationToEdit = annotations.find(a => a.id === annotationId);
    if (annotationToEdit) {
      setEditingAnnotation(annotationToEdit);
    }
  }, [annotations]);

  const handleAnnotationSave = useCallback((annotationId: number, classId: number) => {
    // עדכון התיוג
    const updatedAnnotations = annotations.map(ann =>
      ann.id === annotationId ? { ...ann, class_id: classId } : ann
    );
    setAnnotations(updatedAnnotations);
  }, [annotations]);

  const handleAnnotationUpdate = useCallback((annotationId: number, updates: Partial<Annotation>) => {
    const updatedAnnotations = annotations.map(ann =>
      ann.id === annotationId ? { ...ann, ...updates } : ann
    );
    setAnnotations(updatedAnnotations);

    // Update selected if it's the one being edited
    const updatedAnnotation = updatedAnnotations.find(a => a.id === annotationId);
    if (updatedAnnotation && selectedAnnotation === annotationId) {
      setEditingAnnotation(updatedAnnotation);
    }
  }, [annotations, selectedAnnotation]);

  const selectImage = useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      setCurrentImageIndex(index);
      loadAnnotations(images[index].filename);
    }
  }, [images]);

  // Annotation operations
  const createAnnotation = useCallback((bbox: BoundingBox, classId: number) => {
    const newAnnotation: Annotation = {
      id: Date.now(), // Simple ID generation
      class_id: classId,
      class_name: classes.find(c => c.id === classId)?.name || 'unknown',
      x1: Math.round(bbox.x1),
      y1: Math.round(bbox.y1),
      x2: Math.round(bbox.x2),
      y2: Math.round(bbox.y2),
      width: Math.round(bbox.x2 - bbox.x1),
      height: Math.round(bbox.y2 - bbox.y1)
    };

    setAnnotations(prev => [...prev, newAnnotation]);
    setSelectedAnnotation(newAnnotation.id);
  }, [classes]);

  const deleteAnnotation = useCallback((id: number) => {
    setAnnotations(prev => prev.filter(ann => ann.id !== id));
    setSelectedAnnotation(null);
  }, []);

  const clearAllAnnotations = useCallback(() => {
    setAnnotations([]);
    setSelectedAnnotation(null);
  }, []);

  // Keyboard shortcuts
  const shortcuts = {
    // Navigation
    'arrowleft': goToPrevious,
    'arrowright': goToNext,

    // Tools
    'v': () => setCurrentTool(Tool.POINTER),
    'n': () => setCurrentTool(Tool.BBOX),
    'h': () => setCurrentTool(Tool.PAN),

    // Classes
    '1': () => setCurrentClass(0),
    '2': () => setCurrentClass(1),
    '3': () => setCurrentClass(2),
    '4': () => setCurrentClass(3),

    // Actions
    'cmd+s': saveAnnotations,
    'delete': () => {
      if (selectedAnnotation !== null) {
        deleteAnnotation(selectedAnnotation);
      }
    },
    'backspace': () => {
      if (selectedAnnotation !== null) {
        deleteAnnotation(selectedAnnotation);
      }
    },

    // Clear all (Cmd+K)
    'cmd+k': clearAllAnnotations,

    // Escape to deselect
    'escape': () => setSelectedAnnotation(null)
  };

  useKeyboardShortcuts(shortcuts, !isLoading && !error && directorySet);

  // Show directory selector first
  if (!directorySet) {
    return <DirectorySelector onDirectorySelected={handleDirectorySet} />;
  }

  // Loading state after directory is set
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <div className="text-lg font-medium text-gray-900">Loading annotation tool...</div>
          <div className="text-sm text-gray-500">Loading images from {directoryPath}</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <div className="text-lg font-medium text-gray-900 mb-2">Error Loading Images</div>
          <div className="text-sm text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => {
              setDirectorySet(false);
              setError(null);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mr-2"
          >
            Choose Different Directory
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Restart
          </button>
          <div className="mt-4 text-xs text-gray-500">
            Make sure the Flask backend is running on port 5002
          </div>
        </div>
      </div>
    );
  }

  // No images state
  if (images.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-lg font-medium text-gray-900 mb-2">No Images Found</div>
          <div className="text-sm text-gray-600 mb-4">
            No images found in the selected directory. Please choose a directory with PNG images.
          </div>
          <button
            onClick={() => {
              setDirectorySet(false);
              setError(null);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Choose Different Directory
          </button>
        </div>
      </div>
    );
  }

  const currentImage = images[currentImageIndex];

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        images={images}
        currentImageIndex={currentImageIndex}
        classes={classes}
        currentClass={currentClass}
        currentTool={currentTool}
        stats={stats}
        onImageSelect={selectImage}
        onClassSelect={setCurrentClass}
        onToolSelect={setCurrentTool}
        onSave={saveAnnotations}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onClearAll={clearAllAnnotations}
        onClassEdit={handleClassEdit}
        onClassAdd={handleClassAdd}
        onClassDelete={handleClassDelete}
        isSaving={isSaving}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Status bar */}
        <div className="h-12 bg-white border-b border-gray-200 flex items-center px-4">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">{currentImage.filename}</span>
              <span className="ml-2 text-gray-400">
                {currentImage.width} × {currentImage.height}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>{annotations.length} annotations</span>
            </div>
          </div>

          {isSaving && (
            <div className="ml-auto flex items-center gap-2 text-blue-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Saving...</span>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ImageCanvas
            ref={canvasRef}
            imageUrl={ApiService.getThumbnailUrl(currentImage.filename)}
            imageWidth={currentImage.width}
            imageHeight={currentImage.height}
            annotations={annotations}
            selectedAnnotation={selectedAnnotation}
            currentTool={currentTool}
            currentClass={currentClass}
            onAnnotationCreate={createAnnotation}
            onAnnotationSelect={setSelectedAnnotation}
            onAnnotationDelete={deleteAnnotation}
            onAnnotationEdit={handleAnnotationEdit}
            onAnnotationUpdate={handleAnnotationUpdate}
            transform={transform}
            onTransformChange={setTransform}
          />
          <ZoomControls
            zoom={transform.scale}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onFitToScreen={handleFitToScreen}
            onZoomChange={handleZoomChange}
          />

          {/* Annotation Editor */}
          <AnnotationEditor
            selectedAnnotation={annotations.find(a => a.id === selectedAnnotation) || null}
            annotations={annotations}
            classes={classes}
            transform={transform}
            canvasRef={canvasRef}
            onAnnotationUpdate={handleAnnotationUpdate}
            onAnnotationDelete={deleteAnnotation}
            onAnnotationSelect={setSelectedAnnotation}
          />
        </div>
      </div>

      {/* Class Edit Modal */}
      <ClassEditModal
        isOpen={!!editingClass}
        classInfo={editingClass}
        onSave={handleClassSave}
        onClose={() => setEditingClass(null)}
      />

    </div>
  );
}

export default App;